// src/ConfigEditor.jsx
import validator from '@rjsf/validator-ajv8';
import React, { useState, useEffect } from 'react';
import Form from '@rjsf/core'; // Use core instead of mui until we install the mui package
// import Form from '@rjsf/mui'; // Commented out until package is installed
import MonacoEditor from 'react-monaco-editor';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import { configService } from '../services/config.service';

// Create a custom theme for the configuration editor
const configTheme = createTheme({
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 500,
      letterSpacing: '-0.005em',
    },
    subtitle1: {
      fontWeight: 500,
    },
    body1: {
      lineHeight: 1.6,
    },
  },
  palette: {
    primary: {
      main: '#2563eb', // Modern blue
      light: '#3b82f6',
      dark: '#1d4ed8',
    },
    secondary: {
      main: '#059669', // Teal green
      light: '#10b981',
      dark: '#047857',
    },
    background: {
      default: '#f9fafb',
      paper: '#ffffff',
    },
    text: {
      primary: '#111827',
      secondary: '#4b5563',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.9rem',
        },
      },
    },
  },
});

// Dummy configuration content for demonstration purposes.
const dummyConfigData = {
  database: { host: 'localhost', port: 5432, username: 'admin', password: 'secret' },
  sourcing_rules: {
    po_generation_config: { grouping_strategy: "BY_SUPPLIER", default_lead_time_days: 7 },
    global_defaults: {
      sourcing_algorithm: "DISTANCE",
      allocation: { primary_supplier_percentage: 0.7, secondary_supplier_percentage: 0.3 }
    },
    department_rules: [
      {
        department: "Bakery & Bread",
        sourcing_algorithm: "DISTANCE",
        po_grouping: "byDepartment",
        allocation: { primary_supplier_percentage: 0.8, secondary_supplier_percentage: 0.2 }
      }
      // You can add more rules if desired.
    ]
  }
};

// Utility: Auto-generate a simple JSON Schema based on data keys.
// This fallback schema is used if no external schema is provided.
const buildSchema = (data) => {
  const schema = { type: 'object', properties: {} };
  Object.keys(data).forEach((key) => {
    const value = data[key];
    let type = typeof value;
    if (type === 'number') {
      type = 'number';
    } else if (type === 'boolean') {
      type = 'boolean';
    } else if (Array.isArray(value)) {
      type = 'array';
    } else {
      type = 'string';
    }
    schema.properties[key] = { type, title: key };
  });
  return schema;
};

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`config-tabpanel-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const ConfigEditor = ({ config }) => {
  const [jsonData, setJsonData] = useState({});
  const [schema, setSchema] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [configCategory, setConfigCategory] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch config data using the service
        const configResult = await configService.getConfigByName(config.name);
        
        if (configResult.status === 'success' && configResult.data) {
          setJsonData(configResult.data);
          setConfigCategory(configResult.category || config.category || 'System Configuration');
          
          // Fetch schema if available
          try {
            const schemaResult = await configService.getConfigSchema(config.name);
            if (schemaResult.status === 'success' && schemaResult.data) {
              setSchema(schemaResult.data);
            }
          } catch (schemaError) {
            console.warn('Schema not available:', schemaError);
          }
        } else {
          throw new Error(configResult.message || 'Failed to load configuration');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (config) {
      fetchData();
    }
  }, [config]);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const response = await configService.updateConfig(config.name, {
        data: jsonData,
        config_type: config.name,
        category: config.category
      });

      setSnackbar({
        open: true,
        message: 'Configuration saved successfully',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error saving configuration: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const onFormChange = ({ formData }) => {
    setJsonData(formData);
  };

  const onEditorChange = (newValue) => {
    try {
      const parsed = JSON.parse(newValue);
      setJsonData(parsed);
    } catch (e) {
      // Invalid JSON - don't update the state
    }
  };

  // Format the config name for display
  const formatConfigName = (name) => {
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%" sx={{ py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <ThemeProvider theme={configTheme}>
      <Box sx={{ 
        bgcolor: 'background.default', 
        minHeight: '100vh',
        p: 3
      }}>
        {/* Breadcrumbs navigation */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
          <Link 
            underline="hover" 
            color="inherit" 
            href="/" 
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            underline="hover"
            color="inherit"
            href="/configuration"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <SettingsIcon sx={{ mr: 0.5 }} fontSize="small" />
            Configuration
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: 500 }}>
            {formatConfigName(config.name)}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box>
              <Typography variant="h5" component="h1" gutterBottom>
                {formatConfigName(config.name)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {configCategory}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ 
                px: 3, 
                py: 1,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: alpha(configTheme.palette.primary.main, 0.9),
                }
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
          
          <Divider sx={{ my: 2 }} />
        </Box>

        <Paper 
          elevation={0} 
          sx={{ 
            mb: 3, 
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              bgcolor: alpha(configTheme.palette.primary.main, 0.04),
            }}
            TabIndicatorProps={{
              style: {
                height: 3,
              }
            }}
          >
            <Tab 
              label="Visual Editor" 
              sx={{ 
                py: 1.5,
                px: 3,
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 600,
                }
              }}
            />
            <Tab 
              label="JSON Editor" 
              sx={{ 
                py: 1.5,
                px: 3,
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 600,
                }
              }}
            />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <Box sx={{ 
              bgcolor: 'background.paper', 
              borderRadius: 1,
              p: 2,
            }}>
              <Form
                schema={schema || {
                  type: 'object',
                  properties: Object.keys(jsonData).reduce((acc, key) => {
                    acc[key] = {
                      type: typeof jsonData[key],
                      title: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                    };
                    return acc;
                  }, {})
                }}
                formData={jsonData}
                onChange={onFormChange}
                validator={validator}
                uiSchema={{
                  "ui:submitButtonOptions": {
                    norender: true,
                  }
                }}
              >
                <div />
              </Form>
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Box sx={{ 
              bgcolor: 'background.paper', 
              borderRadius: 1,
              p: 0,
              border: '1px solid',
              borderColor: 'divider',
            }}>
              <MonacoEditor
                language="json"
                theme="vs-light"
                value={JSON.stringify(jsonData, null, 2)}
                options={{
                  selectOnLineNumbers: true,
                  automaticLayout: true,
                  minimap: { enabled: false },
                  fontSize: 14,
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                  wrappingIndent: 'indent',
                  lineNumbers: 'on',
                  renderLineHighlight: 'all',
                  scrollbar: {
                    vertical: 'visible',
                    horizontal: 'visible',
                    useShadows: false,
                  },
                  padding: {
                    top: 16,
                    bottom: 16,
                  }
                }}
                onChange={onEditorChange}
                height="600"
              />
            </Box>
          </TabPanel>
        </Paper>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Chip 
            label={`Last Modified: ${new Date().toLocaleDateString()}`} 
            variant="outlined" 
            size="small"
            sx={{ mr: 1 }}
          />
          <Chip 
            label={`Version: ${config.version || '1.0'}`} 
            variant="outlined" 
            size="small"
          />
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            severity={snackbar.severity} 
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            variant="filled"
            elevation={6}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default ConfigEditor;
